import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { FC, useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  useCreateClientMutation,
  useGetCompanySizesQuery,
} from '@bvi/admin-panel/entities/clients/api-slice';
import { selectCompanySizes } from '@bvi/admin-panel/entities/clients/selectors';
import { IAddClientFormProperties } from '@bvi/admin-panel/feature/clients/ui/add-client-form/types';
import { formatCompanySizeLabel } from '@bvi/api-interfaces/company-size';
import {
  AccountStatus,
  PaymentMethod,
} from '@bvi/api-interfaces/entity/client';
import { ICreateClientRequest } from '@bvi/api-interfaces/request/client';
import { isServerError } from '@bvi/axios-query';
import { Form, FormDatePicker } from '@bvi/common-components';
import { NotistackMessageVariants, useNotistack } from '@bvi/notistack';

import { buildValidationSchema } from './schema';

const ACCOUNT_STATUSES = [
  AccountStatus.DoNotActivate,
  AccountStatus.ReadyToActivate,
  AccountStatus.Active,
];

const PAYMENT_METHODS = [
  PaymentMethod.Ach,
  PaymentMethod.Wire,
  PaymentMethod.CreditCard,
  PaymentMethod.Check,
  PaymentMethod.Other,
];

export const AddClientForm: FC<IAddClientFormProperties> = ({ onSuccess }) => {
  const { data: companySizes, isLoading: isCompanySizesLoading } =
    useGetCompanySizesQuery(undefined, {
      selectFromResult: ({ data, isLoading }) => ({
        data: selectCompanySizes(data),
        isLoading,
      }),
    });

  const [createClientRequest, { isLoading, error }] = useCreateClientMutation();
  const { t } = useTranslation();
  const { showNotificationMessage } = useNotistack();

  const validationSchema = buildValidationSchema(t);
  const methods = useForm<ICreateClientRequest>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      companySizeId: undefined,
      accountStatus: AccountStatus.DoNotActivate,
      subscriptionTermMonths: 12,
      pricingNarrative: '',
    },
  });
  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = methods;

  const subscriptionStartsAt = watch('subscriptionStartsAt');
  const subscriptionTermMonths = watch('subscriptionTermMonths');

  useEffect(() => {
    if (companySizes[0]?.id != null && getValues('companySizeId') == null) {
      setValue('companySizeId', companySizes[0].id, { shouldValidate: false });
    }
  }, [companySizes, getValues, setValue]);

  useEffect(() => {
    if (subscriptionStartsAt == null || !subscriptionTermMonths) {
      return;
    }

    setValue(
      'subscriptionExpiresAt',
      dayjs(subscriptionStartsAt)
        .add(Number(subscriptionTermMonths), 'month')
        .valueOf(),
      { shouldValidate: false },
    );
  }, [subscriptionStartsAt, subscriptionTermMonths, setValue]);

  const onSubmit = async (data: ICreateClientRequest) => {
    try {
      await createClientRequest(data).unwrap();
      onSuccess?.();
    } catch {
      // Error toast is handled via the mutation error effect below.
    }
  };

  useEffect(() => {
    if (isServerError(error)) {
      showNotificationMessage(
        { type: error.type },
        NotistackMessageVariants.ERROR,
      );
    }
  }, [error, showNotificationMessage]);

  return (
    <FormProvider {...methods}>
      <Typography variant="h1" mb={2}>
        {t('clients.add.title')}
      </Typography>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('clients.add.subtitleCompany')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('name')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.name.label')}
              placeholder={t('clients.add.form.name.placeholder')}
              error={Boolean(errors.name)}
              helperText={<>{errors.name?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel
                id="select-company-size"
                error={Boolean(errors.companySizeId)}
              >
                {t('clients.add.form.companySize.label')}
              </InputLabel>
              <Controller
                name="companySizeId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    onChange={(event) =>
                      field.onChange(Number(event.target.value))
                    }
                    label={t('clients.add.form.companySize.label')}
                    labelId="select-company-size"
                    placeholder={t('clients.add.form.companySize.placeholder')}
                    error={Boolean(errors.companySizeId)}
                    disabled={isCompanySizesLoading}
                    MenuProps={{
                      PaperProps: { style: { maxHeight: 420 } },
                    }}
                  >
                    {companySizes.map((companySize) => (
                      <MenuItem key={companySize.id} value={companySize.id}>
                        {formatCompanySizeLabel(companySize.name)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.companySizeId && (
                <FormHelperText error>
                  {errors.companySizeId?.message}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('emailDomain')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.emailDomain.label')}
              placeholder={t('clients.add.form.emailDomain.placeholder')}
              error={Boolean(errors.emailDomain)}
              helperText={<>{errors.emailDomain?.message}</>}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('clients.add.subtitleContacts')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('contactPerson')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.contactPerson.label')}
              placeholder={t('clients.add.form.contactPerson.placeholder')}
              error={Boolean(errors.contactPerson)}
              helperText={<>{errors.contactPerson?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('contactEmail')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.contactEmail.label')}
              placeholder={t('clients.add.form.contactEmail.placeholder')}
              error={Boolean(errors.contactEmail)}
              helperText={<>{errors.contactEmail?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('signatoryName')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.signatoryName.label')}
              placeholder={t('clients.add.form.signatoryName.placeholder')}
              error={Boolean(errors.signatoryName)}
              helperText={<>{errors.signatoryName?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('signatoryEmail')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.signatoryEmail.label')}
              placeholder={t('clients.add.form.signatoryEmail.placeholder')}
              error={Boolean(errors.signatoryEmail)}
              helperText={<>{errors.signatoryEmail?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('billingContactName')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.billingContactName.label')}
              placeholder={t('clients.add.form.billingContactName.placeholder')}
              error={Boolean(errors.billingContactName)}
              helperText={<>{errors.billingContactName?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('billingContactEmail')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.billingContactEmail.label')}
              placeholder={t(
                'clients.add.form.billingContactEmail.placeholder',
              )}
              error={Boolean(errors.billingContactEmail)}
              helperText={<>{errors.billingContactEmail?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('accountManagerName')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.accountManagerName.label')}
              placeholder={t('clients.add.form.accountManagerName.placeholder')}
              error={Boolean(errors.accountManagerName)}
              helperText={<>{errors.accountManagerName?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('accountManagerEmail')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.accountManagerEmail.label')}
              placeholder={t(
                'clients.add.form.accountManagerEmail.placeholder',
              )}
              error={Boolean(errors.accountManagerEmail)}
              helperText={<>{errors.accountManagerEmail?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('supportLiaisonName')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.supportLiaisonName.label')}
              placeholder={t('clients.add.form.supportLiaisonName.placeholder')}
              error={Boolean(errors.supportLiaisonName)}
              helperText={<>{errors.supportLiaisonName?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('supportLiaisonEmail')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.supportLiaisonEmail.label')}
              placeholder={t(
                'clients.add.form.supportLiaisonEmail.placeholder',
              )}
              error={Boolean(errors.supportLiaisonEmail)}
              helperText={<>{errors.supportLiaisonEmail?.message}</>}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('clients.add.subtitleSubscription')}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <FormDatePicker
              field="subscriptionStartsAt"
              label={t('clients.add.form.subscriptionStartsAt.label')}
              error={Boolean(errors.subscriptionStartsAt)}
              helperText={<>{errors.subscriptionStartsAt?.message}</>}
            />
          </Grid>
          <Grid item xs={4}>
            <FormDatePicker
              field="subscriptionExpiresAt"
              label={t('clients.add.form.subscriptionExpiresAt.label')}
              error={Boolean(errors.subscriptionExpiresAt)}
              helperText={<>{errors.subscriptionExpiresAt?.message}</>}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              {...register('subscriptionTermMonths', { valueAsNumber: true })}
              variant="outlined"
              fullWidth
              type="number"
              inputProps={{ min: 1, step: 1 }}
              label={t('clients.add.form.subscriptionTermMonths.label')}
              placeholder={t(
                'clients.add.form.subscriptionTermMonths.placeholder',
              )}
              error={Boolean(errors.subscriptionTermMonths)}
              helperText={<>{errors.subscriptionTermMonths?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel
                id="select-payment-method"
                error={Boolean(errors.paymentMethod)}
              >
                {t('clients.add.form.paymentMethod.label')}
              </InputLabel>
              <Controller
                name="paymentMethod"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    label={t('clients.add.form.paymentMethod.label')}
                    labelId="select-payment-method"
                    error={Boolean(errors.paymentMethod)}
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {t(`clients.paymentMethods.${method}`)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.paymentMethod && (
                <FormHelperText error>
                  {errors.paymentMethod?.message}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel
                id="select-account-status"
                error={Boolean(errors.accountStatus)}
              >
                {t('clients.add.form.accountStatus.label')}
              </InputLabel>
              <Controller
                name="accountStatus"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? AccountStatus.DoNotActivate}
                    label={t('clients.add.form.accountStatus.label')}
                    labelId="select-account-status"
                    error={Boolean(errors.accountStatus)}
                  >
                    {ACCOUNT_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {t(`clients.accountStatuses.${status}`)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.accountStatus && (
                <FormHelperText error>
                  {errors.accountStatus?.message}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              {...register('pricingNarrative')}
              variant="outlined"
              fullWidth
              multiline
              minRows={8}
              label={t('clients.add.form.pricingNarrative.label')}
              placeholder={t('clients.add.form.pricingNarrative.placeholder')}
              error={Boolean(errors.pricingNarrative)}
              helperText={<>{errors.pricingNarrative?.message}</>}
            />
          </Grid>
          <Grid item xs={12} marginTop={1}>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={isLoading}
            >
              {t('clients.add.form.submit')}
            </LoadingButton>
          </Grid>
        </Grid>
      </Form>
    </FormProvider>
  );
};
